import { Injectable } from '@angular/core';
import {environment} from "@environments/environment";
import {RestApiManager} from "./rest-api-manager";
import {Configuration} from "../models/configuration";
import {AccountManagerService} from "./account-manager-service";
import {Utils} from "../utils";
import {Bot} from "../models/bot";
import {TranslateService} from '@ngx-translate/core';
import {CartographicConsoleConfig} from "../models/cartographic-console-config";
import {from, Observable} from "rxjs";
import {map} from "rxjs/operators";

export enum Language {
  EN = 'en',
  IT = 'it'
}

export enum LanguageToString {
  'en' = 'English',
  'it' = 'Italian'
}

@Injectable({
  providedIn: 'root'
})
export class SettingsManagerService {

  public hasCartographicConsole: boolean;
  private config: Configuration;
  private languages = [
    'en', // First is default language
    'it'
  ];

  constructor(
    private accountManagerService: AccountManagerService,
    private restApiManager: RestApiManager,
    private translate: TranslateService
  ) {
    this.getConfig();
  }

  getLogo(white: boolean) {
    return `assets/img/logo/${environment.rebrand.brand}/logo${white ? '-white' : ''}${environment.rebrand.showFullLogo ? '' : '-no-title'}.${environment.rebrand.logoExtension}`;
  }

  async getFileMaxUploadSize(): Promise<number> {
    await this.waitForInitialization();
    return this.config.fileMaxUploadSize * 1024 * 1024;
  }

  async getVideoMaxUploadSize(): Promise<number> {
    await this.waitForInitialization();
    return this.config.videoMaxUploadSize * 1024 * 1024;
  }

  async getSignallers(): Promise<{ byn: string, jitsi: string }> {
    await this.waitForInitialization();
    return this.config.signaller;
  }

  private async getConfig() {
    this.config = await this.restApiManager.get<Configuration>(
      Configuration,
      `${environment.restApi.infoEndpoint}/configuration`
    );
  }

  getBotConfig(): Promise<Bot> {
    return this.restApiManager.get<Bot>(
      Bot,
      `${environment.restApi.endpoint}/companies/${this.accountManagerService.account.company.id}/bot`,
      this.accountManagerService.account.authToken
    );
  }

  setBotConfig(bot: Bot): Promise<Bot> {

    // Check if bot is already created
    if (bot.id) {

      // Update bot
      return this.restApiManager.put<Bot>(
        `${environment.restApi.endpoint}/bot/${bot.id}`,
        { ...bot, companyId: this.accountManagerService.account.company.id },
        this.accountManagerService.account.authToken
      );

    } else {

      // Create bot
      return this.restApiManager.post<Bot>(
        `${environment.restApi.endpoint}/bot`,
        { ...bot, companyId: this.accountManagerService.account.company.id },
        this.accountManagerService.account.authToken
      );

    }
  }

  async getCartographicConsoleConfig(): Promise<CartographicConsoleConfig> {
    const result = await this.restApiManager.get<CartographicConsoleConfig>(
      CartographicConsoleConfig,
      `${environment.restApi.endpoint}/companies/${this.accountManagerService.account.company.id}/external_cartography`,
      this.accountManagerService.account.authToken
    );
    this.hasCartographicConsole = result && result.endpointUrl && result.endpointUrl.length > 0;
    return result;
  }

  setCartographicConsoleConfig(config: CartographicConsoleConfig): Promise<CartographicConsoleConfig> {

    this.hasCartographicConsole = config && config.endpointUrl && config.endpointUrl.length > 0;

    // Check if bot is already created
    if (config.id) {

      // Update config
      return this.restApiManager.put<CartographicConsoleConfig>(
        `${environment.restApi.endpoint}/external_cartography/${config.id}`,
        { ...config, companyId: this.accountManagerService.account.company.id },
        this.accountManagerService.account.authToken
      );

    } else {

      // Create config
      return this.restApiManager.post<CartographicConsoleConfig>(
        `${environment.restApi.endpoint}/external_cartography`,
        { ...config, companyId: this.accountManagerService.account.company.id },
        this.accountManagerService.account.authToken
      );

    }

  }

  initLanguages() {
    this.translate.addLangs(this.languages);
    this.translate.setDefaultLang(this.languages[0]);
    this.setLanguage(this.getLanguage());
  }

  getLanguage(): string {
    return localStorage.getItem('lang') || this.translate.currentLang || this.translate.defaultLang;
  }

  setLanguage(lang: Language | string) {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
  }

  private async waitForInitialization() {
    while(!this.config) {
      await Utils.sleep(500);
    }
  }

}
